import { isEqual } from "lodash";

import type ReactGridLayout from "react-grid-layout";

import {
  type constants,
  type FixedDateInterval,
  type DateRangeChoiceV3,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
  type IGranularity,
} from "@north-beam/nb-common";

export type Level = Exclude<IGranularity, "platform">;

export type RootGridLayout = ReactGridLayout.Layout;

export enum TileType {
  METRIC = "metric",
  BAR_CHART = "bar-chart",
  LINE_CHART = "line-chart",
  CONVERSION_LAG = "conversion-lag",
}

export const TileTypeLabels: { [key in TileType]: string } = {
  [TileType.METRIC]: "Metric",
  [TileType.BAR_CHART]: "Bar Chart",
  [TileType.LINE_CHART]: "Line Chart",
  [TileType.CONVERSION_LAG]: "Conversion Lag",
};

export const getTileTypeLabel = (tileType: TileType): string =>
  TileTypeLabels[tileType];

export type BreakpointName = "xs" | "xxs";

export type Layout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type Layouts = Record<BreakpointName, Layout>;

export const INITIAL_LAYOUTS: Layouts = {
  xs: { x: 0, y: 0, width: 0, height: 0 },
  xxs: { x: 0, y: 0, width: 0, height: 0 },
};

export type ReadyGrid = {
  readonly ref: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  readonly breakpoint: BreakpointName;
  readonly width: number;
  readonly layouts: RootGridLayout[];
  readonly numColumns: number;
};

export type Grid = ReadyGrid;

export const gridIsReady = (grid: Pick<Grid, "width">): grid is ReadyGrid =>
  (grid as ReadyGrid).width !== null;

export interface TileFilter {
  breakdown: string;
  value: string;
}

export type TileDescriptor<T extends TileType = TileType> = T extends TileType
  ? {
      id: string;
      metricId: string;
      metric2Id?: string;
      alignAxes?: boolean;
      sortBreakdowns?: "name" | "value";
      name: string;
      type: T;
      breakdowns: string[];
      breakdownFilters: string[][];
      filters: TileFilter[];
      metric2Filters?: TileFilter[];
      layouts: Layouts;
    }
  : never;

/**
 * Represents the request state that is associated with updating the tiles of a
 * {@link OverviewSavedView}.
 */
export enum TilesSavingState {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  SAVING = "SAVING",
}

export type OverviewFilters = {
  accountingMode: INBAccountingMode;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  level: Level;
  timeGranularity: ITimeGranularityV2;
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  compareDateRange: constants.CompareDateRange | null;
};

export type PageConfiguration = OverviewFilters & {
  tiles: TileDescriptor[];
};

export const DEFAULT_OVERVIEW_FILTERS: OverviewFilters = {
  accountingMode: "accrual",
  attributionModel: "",
  attributionWindow: "1",
  level: "campaign",
  timeGranularity: "daily",
  dateRange: "last7Days",
  compareDateRange: null,
};

export const OVERVIEW_FILTER_KEYS = [
  "accountingMode",
  "attributionModel",
  "attributionWindow",
  "level",
  "timeGranularity",
  "dateRange",
  "compareDateRange",
] as const;

/**
 * Returns a new object, {@link OverviewFilters}, constructed using the filter-related keys of the
 * provided object, {@link PageConfiguration}.
 *
 * @param {PageConfiguration} pageConfiguration
 * @returns {OverviewFilters}
 */
export const parseOverviewFilters = (
  pageConfiguration: PageConfiguration,
): OverviewFilters => {
  return OVERVIEW_FILTER_KEYS.reduce(
    (
      prev: OverviewFilters,
      curr: (typeof OVERVIEW_FILTER_KEYS)[number],
    ): OverviewFilters => ({
      ...prev,
      [curr]: pageConfiguration[curr],
    }),
    {} as OverviewFilters,
  );
};

type OverviewFilterComparator<K extends keyof OverviewFilters> = (
  a: OverviewFilters[K],
  b: OverviewFilters[K],
) => boolean;

const OverviewFilterComparators: {
  [key in keyof OverviewFilters]: OverviewFilterComparator<key>;
} = {
  accountingMode: isEqual,
  attributionModel: isEqual,
  attributionWindow: isEqual,
  timeGranularity: isEqual,
  level: isEqual,
  dateRange: isEqual,
  compareDateRange: isEqual,
};

export const compareOverviewFilters = (
  a: OverviewFilters,
  b: OverviewFilters,
): Set<keyof OverviewFilters> =>
  new Set<keyof OverviewFilters>(
    [...OVERVIEW_FILTER_KEYS].reduce(
      <K extends keyof OverviewFilters>(
        prev: (keyof OverviewFilters)[],
        k: K,
      ) => {
        const comparator: OverviewFilterComparator<K> =
          OverviewFilterComparators[k];
        return comparator(a[k], b[k]) ? prev : [...prev, k];
      },
      [] as (keyof OverviewFilters)[],
    ),
  );

export type OverviewSavedView = {
  id: number;
  name: string;
  is_default: boolean;
  is_template: boolean;
  config: {
    pageConfiguration: PageConfiguration;
  };
};

export enum UndoActionType {
  ADD_NEW_TILE = "ADD_NEW_TILE",
  UPDATE_TILE = "UPDATE_TILE",
  DELETE_TILE = "DELETE_TILE",
  CLONE_TILE = "CLONE_TILE",
}

export type UndoAction = {
  readonly previousState: TileDescriptor[];
  readonly type: UndoActionType;
};
